import { useDispatch } from "react-redux";
import { MutableRefObject, useCallback } from "react";
import { useCompanionStreamingService } from "@hooks/useCompanionStreamingService";
import { HeygenAvatarService } from "@service/avatar/heygen-avatar.service";
import { EventType } from "@models/audio-message";
import {
  pushToTalkAbortStreamMessage,
  pushToTalkEndStreamMessage,
} from "@models/audio-message-stream";
import { setProcessing, setTalking } from "@store/avatarSlice";
import { ConversationPacing } from "@models/conversation-settings";
import { useCompanionConversation } from "@components/Companion/CompanionConversationContext";

const useStreaming = ({
  sessionId,
  avatar,
  ttsEvictionMs,
}: {
  sessionId: string;
  avatar: MutableRefObject<HeygenAvatarService | null>;
  ttsEvictionMs: number;
}): {
  sendMessage: (
    audioBlob?: ArrayBufferLike,
    photoId?: string,
    eventType?: EventType,
    conversationPacing?: ConversationPacing,
  ) => Promise<void>;
  signalPushToTalkEnd: (
    photoId?: string | null,
    conversationPacing?: ConversationPacing,
  ) => Promise<void>;
  signalPushToTalkAbort: (
    photoId?: string | null,
    conversationPacing?: ConversationPacing,
  ) => Promise<void>;
  interruptPlayback: () => Promise<void>;
  suppressGalleryAssistantStream: () => void;
} => {
  const dispatch = useDispatch();
  const { vadBargeInEnabled, voiceInputMode } = useCompanionConversation();

  const speak = useCallback(
    async (text: string) => {
      if (!avatar.current) {
        return;
      }
      await avatar.current.speak(text);
    },
    [avatar],
  );

  const { send, suppressGalleryAssistantStream } = useCompanionStreamingService(
    speak,
    false,
    ttsEvictionMs,
  );

  const interruptPlayback = useCallback(async () => {
    dispatch(setTalking(false));
    dispatch(setProcessing(false));
    await avatar.current?.stopSpeaking();
  }, [avatar, dispatch]);

  const sendMessage = useCallback(
    async (
      audioBlob?: ArrayBufferLike,
      photoId?: string,
      eventType?: EventType,
      conversationPacing?: ConversationPacing,
    ): Promise<void> => {
      try {
        await send({
          audio: audioBlob || null,
          sessionId,
          photoId: photoId || null,
          eventType: eventType || null,
          conversationPacing,
          vadBargeInEnabled,
          voiceInputMode,
        });
      } catch (e) {
        console.error(e);
      }
    },
    [send, sessionId, vadBargeInEnabled, voiceInputMode],
  );

  const signalPushToTalkEnd = useCallback(
    async (
      photoId?: string | null,
      conversationPacingArg?: ConversationPacing,
    ) => {
      if (voiceInputMode !== "push_to_talk") {
        return;
      }
      try {
        await send(
          pushToTalkEndStreamMessage({
            sessionId,
            photoId: photoId ?? null,
            conversationPacing: conversationPacingArg,
            vadBargeInEnabled,
            voiceInputMode,
          }),
        );
      } catch (e) {
        console.error(e);
      }
    },
    [send, sessionId, vadBargeInEnabled, voiceInputMode],
  );

  const signalPushToTalkAbort = useCallback(
    async (
      photoId?: string | null,
      conversationPacingArg?: ConversationPacing,
    ) => {
      if (voiceInputMode !== "push_to_talk") {
        return;
      }
      try {
        await send(
          pushToTalkAbortStreamMessage({
            sessionId,
            photoId: photoId ?? null,
            conversationPacing: conversationPacingArg,
            vadBargeInEnabled,
            voiceInputMode,
          }),
        );
      } catch (e) {
        console.error(e);
      }
    },
    [send, sessionId, vadBargeInEnabled, voiceInputMode],
  );

  return {
    sendMessage,
    signalPushToTalkEnd,
    signalPushToTalkAbort,
    interruptPlayback,
    suppressGalleryAssistantStream,
  };
};

export default useStreaming;
